import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useStories, Story } from '@/hooks/useStories';
import { supabase } from '@/integrations/supabase/client';
import { Upload, Image, Video, Edit } from 'lucide-react';
import { toast } from '@/hooks/use-toast';

interface Profile {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
}

interface StoryEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  story?: Story | null;
  onStoryUpdated?: () => Promise<void>;
  onStoryCreated?: () => Promise<void>;
}

export const StoryEditDialog = ({ 
  open, 
  onOpenChange, 
  story, 
  onStoryUpdated,
  onStoryCreated 
}: StoryEditDialogProps) => {
  const [title, setTitle] = useState('');
  const [duration, setDuration] = useState('86400'); // 24 hours
  const [file, setFile] = useState<File | null>(null);
  const [selectedAuthor, setSelectedAuthor] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [buttonText, setButtonText] = useState('');
  const [altText, setAltText] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [loadingProfiles, setLoadingProfiles] = useState(false);
  const { createStory, updateStory } = useStories();

  const isEditMode = !!story;

  // Load profiles for author selection
  useEffect(() => {
    const loadProfiles = async () => {
      setLoadingProfiles(true);
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('id, first_name, last_name, email')
          .eq('approval_status', 'approved')
          .order('first_name');

        if (error) throw error;
        setProfiles(data || []);
      } catch (error) {
        console.error('Error loading profiles:', error);
        toast({
          title: 'Սխալ',
          description: 'Հեղինակների ցանկը բեռնելու ժամանակ սխալ է տեղի ունեցել',
          variant: 'destructive'
        });
      } finally {
        setLoadingProfiles(false);
      }
    };

    if (open) {
      loadProfiles();
    }
  }, [open]);

  // Initialize form when story changes
  useEffect(() => {
    if (story && isEditMode) {
      setTitle(story.title || '');
      setDuration(story.duration.toString());
      setSelectedAuthor(story.user_id);
      setLinkUrl(story.link_url || '');
      setButtonText(story.button_text || '');
      setAltText(story.alt_text || '');
      setFile(null); // Don't set file for edit mode
    } else {
      // Reset form for create mode
      setTitle('');
      setDuration('86400');
      setSelectedAuthor('');
      setLinkUrl('');
      setButtonText('');
      setAltText('');
      setFile(null);
    }
  }, [story, isEditMode, open]);

  const handleFileSelect = (files: FileList | null) => {
    if (files && files[0]) {
      const selectedFile = files[0];
      
      // Check file size (100MB limit)
      const maxSize = 100 * 1024 * 1024; // 100MB in bytes
      if (selectedFile.size > maxSize) {
        toast({
          title: 'Սխալ',
          description: 'Ֆայլի չափը չպետք է գերազանցի 100 ՄԲ-ը',
          variant: 'destructive'
        });
        return;
      }

      // Check file type
      if (!selectedFile.type.match(/^(image|video)\//)) {
        toast({
          title: 'Սխալ',
          description: 'Խնդրում ենք ընտրել միայն ֆոտո կամ վիդեո ֆայլ',
          variant: 'destructive'
        });
        return;
      }

      setFile(selectedFile);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isEditMode) {
      // Update existing story
      if (!story) return;
      
      setIsUploading(true);
        try {
          console.log('Updating existing story with:', { 
            title, 
            duration: parseInt(duration), 
            selectedAuthor, 
            linkUrl, 
            buttonText, 
            altText 
          });

          const newDuration = parseInt(duration);
          const updates: Partial<Story> = {
            title,
            duration: newDuration,
            expires_at: new Date(Date.now() + newDuration * 1000).toISOString(),
            user_id: selectedAuthor || story.user_id,
            link_url: linkUrl || null,
            button_text: buttonText || null,
            alt_text: altText || null
          };

          console.log('Final update object:', updates);

          await updateStory(story.id, updates);
          onOpenChange(false);
          
          if (onStoryUpdated) {
            await onStoryUpdated();
          }
        } catch (error) {
        console.error('Error updating story:', error);
      } finally {
        setIsUploading(false);
      }
    } else {
      // Create new story
      if (!file) {
        toast({
          title: 'Սխալ', 
          description: 'Խնդրում ենք ընտրել ֆայլ',
          variant: 'destructive'
        });
        return;
      }

      if (!selectedAuthor) {
        toast({
          title: 'Սխալ',
          description: 'Խնդրում ենք ընտրել հեղինակ',
          variant: 'destructive'
        });
        return;
      }

      setIsUploading(true);
      try {
        // Create story with selected author
        await createStory(
          file, 
          title, 
          parseInt(duration), 
          selectedAuthor,
          linkUrl || undefined,
          buttonText || undefined,
          altText || undefined
        );
        onOpenChange(false);
        setTitle('');
        setFile(null);
        setDuration('86400');
        setSelectedAuthor('');
        setLinkUrl('');
        setButtonText('');
        setAltText('');
        
        if (onStoryCreated) {
          await onStoryCreated();
        }
      } catch (error) {
        console.error('Error creating story:', error);
      } finally {
        setIsUploading(false);
      }
    }
  };

  const durationOptions = [
    { value: '3600', label: '1 ժամ' },
    { value: '21600', label: '6 ժամ' },
    { value: '43200', label: '12 ժամ' },
    { value: '86400', label: '24 ժամ' },
    { value: '172800', label: '2 օր' },
    { value: '604800', label: '1 շաբաթ' }
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isEditMode ? <Edit size={20} /> : <Upload size={20} />}
            {isEditMode ? 'Խմբագրել պատմությունը' : 'Նոր պատմություն'}
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {!isEditMode && (
            <div className="space-y-2">
              <Label htmlFor="story-file">Ֆայլ</Label>
              <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-6 text-center">
                {file ? (
                  <div className="space-y-2">
                    <div className="flex items-center justify-center">
                      {file.type.startsWith('image/') ? (
                        <Image className="w-8 h-8 text-green-500" />
                      ) : (
                        <Video className="w-8 h-8 text-blue-500" />
                      )}
                    </div>
                    <p className="text-sm font-medium">{file.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {(file.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setFile(null)}
                    >
                      Փոխել
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Upload className="w-8 h-8 text-muted-foreground mx-auto" />
                    <div>
                      <Input
                        id="story-file"
                        type="file"
                        accept="image/*,video/*"
                        onChange={(e) => handleFileSelect(e.target.files)}
                        className="hidden"
                      />
                      <Label
                        htmlFor="story-file"
                        className="cursor-pointer text-sm text-primary hover:underline"
                      >
                        Ընտրել ֆայլ
                      </Label>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Ֆոտո կամ վիդեո (առավելագույնը 100 ՄԲ)
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="author">Հեղինակ</Label>
            <Select 
              value={selectedAuthor} 
              onValueChange={setSelectedAuthor}
              disabled={loadingProfiles}
            >
              <SelectTrigger>
                <SelectValue placeholder={loadingProfiles ? "Բեռնվում է..." : "Ընտրել հեղինակ"} />
              </SelectTrigger>
              <SelectContent>
                {profiles.map(profile => (
                  <SelectItem key={profile.id} value={profile.id}>
                    {profile.first_name} {profile.last_name} ({profile.email})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="title">Վերնագիր (ոչ պարտադիր)</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ավելացրեք վերնագիր..."
              maxLength={100}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="duration">Տևողություն</Label>
            <Select value={duration} onValueChange={setDuration}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {durationOptions.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="link-url">Հղում (ոչ պարտադիր)</Label>
            <Input
              id="link-url"
              type="url"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              placeholder="https://example.com"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="button-text">Կոճակի տեքստ (ոչ պարտադիր)</Label>
            <Input
              id="button-text"
              value={buttonText}
              onChange={(e) => setButtonText(e.target.value)}
              placeholder="Ավելին իմանալ"
              maxLength={30}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="alt-text">Ալտերնատիվ տեքստ (ոչ պարտադիր)</Label>
            <Textarea
              id="alt-text"
              value={altText}
              onChange={(e) => setAltText(e.target.value)}
              placeholder="Նկարի նկարագրություն"
              maxLength={200}
              rows={2}
            />
          </div>

          <div className="flex gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1"
            >
              Չեղարկել
            </Button>
            <Button
              type="submit"
              disabled={(!file && !isEditMode) || isUploading || !selectedAuthor}
              className="flex-1"
            >
              {isUploading 
                ? (isEditMode ? 'Թարմացնում...' : 'Վերբեռնում...') 
                : (isEditMode ? 'Թարմացնել' : 'Հրապարակել')
              }
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};